import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';

const ThemeContext = createContext(null);
const browserThemeColors = { light: '#f7f6f2', dark: '#191c19' };

const getSystemTheme = () =>
  window.matchMedia?.('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';

export function ThemeProvider({ children }) {
  const [theme, setTheme] = useState(() => {
    const saved = localStorage.getItem('siteTheme');
    if (['light', 'dark', 'system'].includes(saved)) return saved;

    const legacy = localStorage.getItem('articleTheme');
    return ['light', 'dark'].includes(legacy) ? legacy : 'system';
  });
  const [systemTheme, setSystemTheme] = useState(getSystemTheme);

  useEffect(() => {
    const media = window.matchMedia?.('(prefers-color-scheme: dark)');
    if (!media) return undefined;
    const handleChange = () => setSystemTheme(media.matches ? 'dark' : 'light');
    media.addEventListener?.('change', handleChange);
    return () => media.removeEventListener?.('change', handleChange);
  }, []);

  const resolvedTheme = theme === 'system' ? systemTheme : theme;

  useEffect(() => {
    const root = document.documentElement;
    root.dataset.theme = resolvedTheme;
    root.dataset.themePreference = theme;
    root.style.colorScheme = resolvedTheme;
    document.querySelector('meta[name="theme-color"]')
      ?.setAttribute('content', browserThemeColors[resolvedTheme]);
    localStorage.setItem('siteTheme', theme);
    localStorage.setItem('articleTheme', resolvedTheme);
  }, [resolvedTheme, theme]);

  const value = useMemo(
    () => ({ theme, setTheme, resolvedTheme, isDark: resolvedTheme === 'dark' }),
    [theme, resolvedTheme],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (!context) throw new Error('useTheme must be used inside ThemeProvider');
  return context;
}
